// One pane's in-pane search state (#4401, epic #4391), pulled out of
// `TerminalPane.tsx` (size budget, and one reason to change). `search.ts`
// holds the pure matching; this is where it meets signals.
//
// FIND, NOT FILTER: every line stays in place. The current match is tracked by
// ID, never by index, so rows appended by the live tail or evicted from the
// front of the ring buffer never shift it onto a different line. A new query
// lands on the NEWEST match: the bottom, where a terminal reader is looking.

import type { TerminalEvent } from '@developerz/domain';
import { type Accessor, createEffect, createMemo, createSignal, on } from 'solid-js';
import { findMatches, stepMatch } from './search';

export type PaneSearch = {
  readonly open: Accessor<boolean>;
  readonly query: Accessor<string>;
  readonly setQuery: (query: string) => void;
  readonly matchIds: Accessor<ReadonlySet<string>>;
  readonly activeMatchId: Accessor<string | null>;
  /** 1-based position of the current match, or null. */
  readonly current: Accessor<number | null>;
  readonly total: Accessor<number>;
  /** Bumped when an already-open bar is asked for again, so it re-focuses. */
  readonly focusRequest: Accessor<number>;
  readonly step: (direction: 1 | -1) => void;
  readonly close: () => void;
};

export function usePaneSearch(
  rows: Accessor<readonly TerminalEvent[]>,
  request: Accessor<number>,
): PaneSearch {
  const [open, setOpen] = createSignal(false);
  const [query, setQuery] = createSignal('');
  const [activeMatchId, setActiveMatchId] = createSignal<string | null>(null);
  const [focusRequest, setFocusRequest] = createSignal(0);
  const matches = createMemo(() => (open() ? findMatches(rows(), query()) : []));
  const matchIds = createMemo(() => new Set(matches()));
  const position = createMemo(() => {
    const at = matches().indexOf(activeMatchId() ?? '');
    return at === -1 ? null : at;
  });

  createEffect(
    on(query, () => {
      const found = matches();
      setActiveMatchId(found[found.length - 1] ?? null);
    }),
  );
  createEffect(
    on(
      request,
      () => {
        setOpen(true);
        setFocusRequest((n) => n + 1);
      },
      { defer: true },
    ),
  );

  return {
    open,
    query,
    setQuery,
    matchIds,
    activeMatchId,
    current: () => {
      const at = position();
      return at === null ? null : at + 1;
    },
    total: () => matches().length,
    focusRequest,
    step: (direction) => {
      const found = matches();
      const next = stepMatch(position(), found.length, direction);
      setActiveMatchId(next === null ? null : (found[next] ?? null));
    },
    close: () => {
      setOpen(false);
      setQuery('');
      setActiveMatchId(null);
    },
  };
}
